import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import MultiSelectDropdown from "@/components/generics/MultiSelectDropdown.vue";
import SalesChartsRow from "@/components/financialReports/SalesChartsRow.vue";
import ExpensesEvolutionTable from "@/components/financialReports/ExpensesEvolutionTable.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/HeaderClientDetail.vue";
import BodyClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/BodyClientDetail.vue";
import { obligationForType } from "@/utils/counterpartObligation";
import { PERIOD_TYPES, FILTER_PERIOD_OPTIONS, TOP_CLIENTS_COUNT } from "@/config/reportStructure";
import { defaultYearRange } from "@/store/financialReports/utils/dateRange";
import { periodLastMonth } from "@/views/financialReports/utils/periodFormatting";
import { capitalize } from "@/utils/string";

// Factory de las opciones del tab de "Breakdown de <X>" (Costos / Gastos). Los
// dos tabs eran ~95% idénticos: misma tabla de evolución + gráficos del header +
// panel de comparación + drill al modal de análisis + atajo al detalle de
// contraparte. Esta factory devuelve el objeto de opciones del componente; cada
// SFC aporta su propio <template> (theme/título/emptyMessage) y exporta
// `createBreakdownTab(config)` como su `export default`.
//
// @param {object} config
// @param {string} config.name             nombre del componente ("CostosTab")
// @param {string} config.theme            data-theme propagado a los modals ("costos")
// @param {string} config.sectionNoun      sustantivo del título de gráficos ("Costos")
// @param {string} config.level1Name       nivel 1 para openClientDetail ("Costos")
// @param {string} config.drillTab         id del tab para el pendingDrill ("costos")
// @param {string} config.reportNs         namespace del store de reporte ("costsReport")
// @param {string} config.analysisNs       namespace del store de análisis ("costsAnalysis")
// @param {Array}  config.counterpartTypes opciones del dropdown de contrapartes
// @param {string} config.openAnalysisName nombre del método de drill ("openCostsAnalysis")
// @param {object} config.headerComp       componente header del modal de análisis
// @param {object} config.bodyComp         componente body del modal de análisis
// @param {object} config.actions          { data, charts, comparison, analysis } nombres reales
export function createBreakdownTab(config) {
    const {
        name,
        theme,
        sectionNoun,
        level1Name,
        drillTab,
        reportNs,
        analysisNs,
        counterpartTypes,
        openAnalysisName,
        headerComp,
        bodyComp,
        actions: A,
    } = config;

    return {
        name,

        components: {
            TabHeader,
            CollapsibleSection,
            MultiSelectDropdown,
            SalesChartsRow,
            ExpensesEvolutionTable,
        },

        data() {
            return {
                periodOptions: PERIOD_TYPES,
                counterpartTypeOptions: counterpartTypes,
                expandedL2Rows: [],
                filterOptions: FILTER_PERIOD_OPTIONS,
                // "% de ventas": un solo toggle (en SalesChartsRow) que cambia el
                // chart de líneas y la tabla a porcentajes sobre ventas. Compartido.
                percentMode: false,
                // Panel de comparación abierto (toggle de la tabla). Lo trackeamos
                // para refetchear la comparación al cambiar el mes sólo si está visible.
                comparisonOpen: false,
            };
        },

        computed: {
            ...mapState("classification", ["categories"]),
            ...mapState("reportsFilters", ["endMonth", "selectedPeriodRange", "customRange", "customYearRange", "pendingDrill"]),
            ...mapState("financialReports", ["filterPeriod"]),
            ...mapState(reportNs, [
                "activeSubFilter",
                "selectedCounterpartTypes",
                "selectedItems",
                "level2Accounts",
                "hierarchy",
                "counterpartTypes",
                "availableCategories",
                "monthHeaders",
                "salesByPeriod",
                "isLoading",
                "error",
                "isChartsLoading",
                "chartsError",
                "comparisonData",
                "isComparisonLoading",
                "comparisonError",
                "comparisonYears",
            ]),
            // Lente activo del modal de análisis — el header puede cambiar el mes
            // y los filtros; retenemos account/selectedItems/month para el refetch.
            ...mapState(analysisNs, {
                analysisAccount: "account",
                analysisAccountLevel: "accountLevel",
                analysisSelectedItems: "selectedItems",
                analysisMonth: "month",
            }),
            ...mapGetters(reportNs, [
                "tableRows", "totalRow", "ratioRow", "isCuentasMode", "headerChartsData",
            ]),

            getNow() {
                return moment().toDate();
            },

            // Rango activo del calendar del header (modo range). Si el store tiene
            // customRange lo usamos; si no, derivamos un default (últimos 12 meses
            // sobre endMonth) para que el picker abra pintando "Último año".
            calendarRange() {
                if (this.customRange) return this.customRange;
                const end = moment(this.endMonth).startOf("month");
                return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
            },

            // Rango de años activo del picker de Anual: la selección custom o el
            // default de Anual (mismo `defaultYearRange` que usa el fetch), para
            // que el trigger abra pintando el span real (p.ej. "2024 – 2026") en
            // vez de "—" y nunca difiera de lo que muestra la tabla.
            yearRange() {
                return this.customYearRange || defaultYearRange(this.endMonth);
            },

            // Etiqueta del total al pie del tooltip del line chart en percentMode.
            // El pie muestra "monto / %", donde el % es Σ series / ventas netas =
            // la fila "% Costos/Ventas" de la tabla. Lo nombramos "Total" — nunca
            // "Total ventas" (que es de Ventas, no de Costos/Gastos).
            chartTotalLabel() {
                return "Total";
            },

            // Título dinámico de la sección de gráficos según la dimensión activa.
            chartsSectionTitle() {
                if (this.isCuentasMode) return `${sectionNoun} por cuenta`;
                if (this.activeSubFilter === "contrapartes") return `${sectionNoun} por contraparte`;
                const singular = this.activeSubFilter.replace(/s$/i, "").toLowerCase();
                return `${sectionNoun} por ${singular}`;
            },

            headerChartsLoading() {
                return this.isChartsLoading;
            },
            headerChartsError() {
                return this.chartsError || "";
            },

            // Opciones del filtro multi-select del header. En contrapartes es plano
            // (las mismas filas que la tabla); en cuentas/categoría agrupado por L2.
            itemFilterOptions() {
                if (this.activeSubFilter === "contrapartes") {
                    return this.tableRows.map((row) => ({ id: row.label, label: row.label }));
                }
                const options = [];
                this.level2Accounts.forEach((l2) => {
                    (this.hierarchy[l2] || []).forEach((l3) => {
                        if (l3 === l2) return;
                        options.push({ id: l3, label: l3, group: l2 });
                    });
                });
                return options;
            },

            itemFilterPlaceholder() {
                if (this.isCuentasMode) return "Filtrar cuentas";
                if (this.activeSubFilter === "contrapartes") return "Filtrar contrapartes";
                return `Filtrar ${this.activeSubFilter.toLowerCase()}`;
            },

            // Períodos visibles en el header. YTD/Anual no aplican a la comparación,
            // así que con el panel abierto sólo dejamos Mensual y Trimestral.
            visiblePeriodOptions() {
                if (!this.comparisonOpen) return this.filterOptions;
                return this.filterOptions.filter((o) => o.id === "month" || o.id === "quarter");
            },

            // View-model que se pasa entero a ExpensesEvolutionTable → SalesComparisonChart.
            // No hay subcategorías → subItems vacío, selectedItem null.
            comparisonViewModel() {
                return {
                    series: this.comparisonData ? this.comparisonData.series : [],
                    months: this.comparisonData ? this.comparisonData.months : [],
                    availableYears: this.comparisonData ? this.comparisonData.availableYears : [],
                    isLoading: this.isComparisonLoading,
                    error: this.comparisonError || "",
                    subItems: [],
                    selectedItem: null,
                    years: this.comparisonYears,
                    period: this.filterPeriod,
                };
            },

            visibleRows() {
                // Contrapartes: tabla plana (una fila por contraparte, sin anidar ni
                // expandir/colapsar). El filtro de items, si hay, acota la lista.
                if (this.activeSubFilter === "contrapartes") {
                    if (!this.selectedItems.length) return this.tableRows;
                    const selected = new Set(this.selectedItems);
                    return this.tableRows.filter((row) => selected.has(row.label));
                }
                // Sin filtro de items: L2 siempre visibles, L3 según la expansión.
                if (!this.selectedItems.length) {
                    return this.tableRows.filter((row) => {
                        if (row.level === 2) return true;
                        return this.expandedL2Rows.includes(row.parent);
                    });
                }
                // Con filtro: sólo las L3 seleccionadas (dentro de su L2 expandida) y
                // las L2 que tengan al menos una L3 seleccionada.
                const selected = new Set(this.selectedItems);
                const l2WithSelected = new Set(
                    this.tableRows
                        .filter((row) => row.level === 3 && selected.has(row.label))
                        .map((row) => row.parent),
                );
                return this.tableRows.filter((row) => {
                    if (row.level === 2) return l2WithSelected.has(row.label);
                    return selected.has(row.label) && this.expandedL2Rows.includes(row.parent);
                });
            },

            // Firma reactiva consolidada — un solo trigger para fetch aunque varios
            // filtros cambien en la misma transacción.
            fetchSignature() {
                return [
                    this.endMonth,
                    this.selectedPeriodRange,
                    this.activeSubFilter,
                    this.selectedCounterpartTypes.join(","),
                    this.filterPeriod,
                    this.customRange && this.customRange.from,
                    this.customRange && this.customRange.to,
                    this.customYearRange && this.customYearRange.from,
                    this.customYearRange && this.customYearRange.to,
                ].join("|");
            },
        },

        methods: {
            ...mapActions(reportNs, [A.data, A.charts, A.comparison]),
            ...mapActions(analysisNs, [A.analysis]),
            // Drawer de detalle de contraparte (modal 2). Mismo módulo que usa el
            // Body del análisis al clickear una contraparte del ranking.
            ...mapActions("clientDetail", {
                openClientDetail: "openClientDetail",
                closeClientDetail: "closeClientDetail",
                changeClientRangePeriod: "changeRangePeriod",
            }),
            ...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
            // Categorías de clasificación (tiendas/marcas/…) son globales; la action
            // hace short-circuit si ya las cargó otro view.
            ...mapActions("classification", ["getCategories"]),
            ...mapMutations("reportsFilters", ["SET_END_MONTH", "SET_PENDING_DRILL"]),
            ...mapMutations(reportNs, [
                "SET_ACTIVE_SUB_FILTER",
                "SET_SELECTED_COUNTERPART_TYPES",
                "SET_SELECTED_ITEMS",
                "SET_COMPARISON_YEARS",
            ]),
            capitalize,
            ...mapMutations(analysisNs, { resetAnalysis: "RESET" }),
            ...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

            updateEndMonth(date) {
                this.SET_END_MONTH(date);
            },
            updateFilterPeriod(period) {
                this.SET_FILTER_PERIOD(period);
            },
            // El calendar del header (modo range) emite el rango; la action sincroniza
            // customRange + selectedPeriodRange="custom" + endMonth.
            updateDateRange(range) {
                this.setCustomRange(range);
            },
            // El picker de Anual emite el rango de años; la action lo guarda
            // (sin tocar endMonth/selectedPeriodRange — es estado ortogonal).
            updateYearRange(range) {
                this.setCustomYearRange(range);
            },
            // Cambia la dimensión del header (cuentas / categoría). Contrapartes se
            // activa por su propio dropdown (SET_SELECTED_COUNTERPART_TYPES), no acá.
            setSubFilter(filter) {
                this.SET_ACTIVE_SUB_FILTER(filter);
            },
            // Categoría sin clasificación real en el período (sólo "Sin
            // <categoría>") → chip deshabilitado: agruparla mostraría sólo el
            // total repetido y los charts una sola serie. El backend confirma
            // cuáles tienen datos en `availableCategories`.
            isCategoryDisabled(category) {
                return !this.availableCategories.includes(category);
            },
            toggleL2(label) {
                const index = this.expandedL2Rows.indexOf(label);
                if (index > -1) {
                    this.expandedL2Rows.splice(index, 1);
                } else {
                    this.expandedL2Rows.push(label);
                }
            },

            // --- Comparación año-contra-año ---
            onToggleComparison(open) {
                this.comparisonOpen = open;
                if (!open) return;
                if (this.filterPeriod !== "month" && this.filterPeriod !== "quarter") {
                    this.SET_FILTER_PERIOD("month");
                }
                this[A.comparison]();
            },
            onComparisonAddYear(year) {
                this.SET_COMPARISON_YEARS([...this.comparisonYears, year]);
                this[A.comparison]();
            },
            onComparisonRemoveYear(year) {
                this.SET_COMPARISON_YEARS(
                    this.comparisonYears.filter((y) => y !== year),
                );
                this[A.comparison]();
            },

            // --- Modal de Análisis -----------------------------------------------

            // Cell-click de la tabla → abre el drawer de análisis del mes con la
            // cuenta clickeada pre-seleccionada en la card del estado (card 1), que
            // recorta el ranking de contrapartes a esa cuenta. Una fila L2 (grupo)
            // es siempre una cuenta del plan; una L3 sólo lo es en modo cuentas (en
            // categorías la L3 es un atributo y en contrapartes la fila es una
            // contraparte) → en esos casos, y en la fila Total, arranca con todas.
            [openAnalysisName]({ monthLabel, account, level }) {
                // El modal es mensual: en quarter/year/ytd abre el ÚLTIMO mes del
                // período clickeado (capeado a endMonth).
                const month = periodLastMonth(monthLabel, this.filterPeriod, this.endMonth);
                if (!month) return;

                // En contrapartes la tabla lista contrapartes: el clic en una fila va
                // directo al detalle de la contraparte (modal 2), saltando el análisis
                // del mes (modal 1). La fila Total (sin account) cae al análisis general.
                if (this.activeSubFilter === "contrapartes" && account) {
                    this.openCounterpartDetailFromCell({
                        counterpart: account,
                        month,
                        counterpartType: this.counterpartTypes[account] || null,
                    });
                    return;
                }

                let drillAccount = null;
                let drillLevel = null;
                if (level === 2) {
                    drillAccount = account;
                    drillLevel = "2";
                } else if (level === 3 && this.isCuentasMode) {
                    drillAccount = account;
                    drillLevel = "3";
                }

                const monthDate = moment(month, "YYYY-MM").toDate();
                const periodLabel = moment(month, "YYYY-MM")
                    .locale("es")
                    .format("MMM YYYY")
                    .replace(/^./, (c) => c.toUpperCase());

                this[A.analysis]({ month, account: drillAccount, accountLevel: drillLevel, groupBy: null, selectedItems: {} });
                this.$buefy.modal.open({
                    parent: this,
                    hasModalCard: true,
                    customClass: "wide-summary-modal",
                    canCancel: ["escape", "outside"],
                    component: ModalFullHeightRight,
                    props: {
                        headerComp,
                        bodyComp,
                        headerProps: {
                            periodLabel,
                            endMonth: monthDate,
                            getNow: this.getNow,
                        },
                        bodyProps: null,
                        // El modal se monta en document.body — sin esto el subárbol no
                        // hereda el [data-theme] del tab.
                        theme,
                    },
                    events: {
                        closed: this.onAnalysisClose,
                        "update-end-month": this.onAnalysisMonthChange,
                    },
                });
            },

            // Atajo de contrapartes: abre directo el drawer de detalle de la
            // contraparte clickeada en la tabla (modal 2). El tipo viene del breakdown
            // (counterpartTypes) → obligación + filtrado correctos.
            openCounterpartDetailFromCell({ counterpart, month, counterpartType }) {
                this.openClientDetail({
                    alias: counterpart,
                    documentType: "P",
                    monthIso: month,
                    obligationName: obligationForType(counterpartType),
                    level1Name,
                    counterpartType,
                });
                this.$buefy.modal.open({
                    parent: this,
                    hasModalCard: true,
                    customClass: "wide-summary-modal",
                    canCancel: ["escape", "outside"],
                    component: ModalFullHeightRight,
                    props: {
                        headerComp: HeaderClientDetail,
                        bodyComp: BodyClientDetail,
                        headerProps: { fallbackAlias: counterpart },
                        bodyProps: null,
                        // Primer drawer del flujo (no apilado) → ancho default 85%.
                        theme,
                        width: "85%",
                    },
                    events: {
                        closed: this.closeClientDetail,
                        "update-range-period": this.changeClientRangePeriod,
                    },
                });
            },

            // El usuario cambió el mes desde el AnualCalendar del header del drawer.
            // account/level/selectedItems se conservan — sólo cambia el período.
            onAnalysisMonthChange(date) {
                const month = moment(date).format("YYYY-MM");
                this[A.analysis]({
                    month,
                    account: this.analysisAccount,
                    accountLevel: this.analysisAccountLevel,
                    groupBy: null,
                    selectedItems: this.analysisSelectedItems,
                });
            },

            // Cierre del drawer: resetea el lente de análisis.
            onAnalysisClose() {
                this.resetAnalysis();
            },

            // Consume el pendingDrill (seteado por ResultadosTab al clickear una
            // celda de este nivel en el P&L). Abre el modal del mes pedido para el
            // total (account null). Espera a las categorías para que el header del
            // drawer tenga sus chips listos.
            async consumePendingDrill() {
                const drill = this.pendingDrill;
                if (!drill || drill.tab !== drillTab) return;
                this.SET_PENDING_DRILL(null);
                await this.getCategories();
                this[openAnalysisName]({ monthLabel: drill.monthLabel, account: null, level: null });
            },
        },

        mounted() {
            this[A.data]();
            this[A.charts]({ topN: TOP_CLIENTS_COUNT });
            // Alimenta los chips de categoría del header (short-circuit si ya
            // cargaron por otro view).
            this.getCategories();
            // Drill cross-tab: si llegó clickeando una celda de este nivel en el P&L.
            this.consumePendingDrill();
        },

        watch: {
            // Tabla y gráficos comparten los mismos filtros: un solo trigger refetchea ambos.
            fetchSignature() {
                this[A.data]();
                this[A.charts]({ topN: TOP_CLIENTS_COUNT });
            },
            // Por default todas las cuentas L2 arrancan desplegadas tras cada fetch.
            tableRows: {
                immediate: true,
                handler(rows) {
                    this.expandedL2Rows = rows
                        .filter((row) => row.level === 2)
                        .map((row) => row.label);
                },
            },
            // El mes del header redefine el cutoff del año en curso de la comparación
            // — refetch sólo si el panel está abierto.
            endMonth() {
                if (this.comparisonOpen) this[A.comparison]();
            },
        },
    };
}
